import React from "react";

/**
 * AIAgentLoader — full-screen blocking overlay shown while a long-running
 * AI-powered action is in flight (e.g. running a scan, running advisor).
 *
 * Visual: frosted backdrop · centered card with "AI AGENT" pill, two
 * counter-rotating concentric arcs, pulsing central dot, title + subtitle.
 *
 * Use:
 *   <AIAgentLoader
 *      open={loading}
 *      title="Scanning your cloud accounts"
 *      subtitle="Hunting waste across BigQuery, Compute, GKE, Vertex AI…"
 *   />
 */
export default function AIAgentLoader({
  open,
  title = "AI Agent is working",
  subtitle = "Crunching telemetry to surface the best recommendations.",
  testId = "ai-agent-loader",
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ background: "rgba(59, 30, 102, 0.30)", backdropFilter: "blur(6px)" }}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <style>{`
        @keyframes ai-spin-cw   { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
        @keyframes ai-spin-ccw  { from { transform: rotate(0deg); }   to { transform: rotate(-360deg); } }
        @keyframes ai-pulse     { 0%,100% { transform: scale(1); opacity: .85 } 50% { transform: scale(1.18); opacity: 1 } }
        @keyframes ai-aura      { 0%,100% { opacity: .25 } 50% { opacity: .55 } }
        @keyframes ai-card-in   { from { opacity: 0; transform: translateY(8px) scale(.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>

      <div
        className="bg-white rounded-2xl shadow-[0_20px_60px_-12px_rgba(76,29,149,0.35)] p-8 sm:p-10 w-full max-w-md text-center"
        style={{ animation: "ai-card-in .35s ease-out both" }}
      >
        {/* Pill */}
        <span
          className="inline-block text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
          style={{
            background: "linear-gradient(90deg, #ede9fe 0%, #f3e8ff 100%)",
            color: "#6d28d9",
          }}
        >
          AI Agent
        </span>

        {/* Rings + core */}
        <div className="relative mx-auto mt-6 mb-6" style={{ width: 180, height: 180 }}>
          {/* Outer purple arc */}
          <svg
            className="absolute inset-0"
            viewBox="0 0 100 100"
            style={{ animation: "ai-spin-cw 2.4s linear infinite" }}
          >
            <defs>
              <linearGradient id="ai-grad-purple" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#a78bfa" stopOpacity="0" />
                <stop offset="55%" stopColor="#7c3aed" />
                <stop offset="100%" stopColor="#6d28d9" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="46" fill="none" stroke="#ede9fe" strokeWidth="2" />
            <circle
              cx="50" cy="50" r="46"
              fill="none" stroke="url(#ai-grad-purple)"
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray="120 200"
            />
          </svg>

          {/* Inner green arc (counter-rotating) */}
          <svg
            className="absolute inset-0"
            viewBox="0 0 100 100"
            style={{ animation: "ai-spin-ccw 3.2s linear infinite" }}
          >
            <defs>
              <linearGradient id="ai-grad-green" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#86efac" stopOpacity="0" />
                <stop offset="55%" stopColor="#22c55e" />
                <stop offset="100%" stopColor="#15803d" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="34" fill="none" stroke="#dcfce7" strokeWidth="2" />
            <circle
              cx="50" cy="50" r="34"
              fill="none" stroke="url(#ai-grad-green)"
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray="80 160"
            />
          </svg>

          {/* Aura behind core */}
          <div
            className="absolute rounded-full"
            style={{
              top: "50%", left: "50%", width: 86, height: 86,
              transform: "translate(-50%,-50%)",
              background: "radial-gradient(closest-side, rgba(167,139,250,0.55), rgba(167,139,250,0))",
              animation: "ai-aura 2.4s ease-in-out infinite",
            }}
          />
          {/* Core dot */}
          <div
            className="absolute rounded-full"
            style={{
              top: "50%", left: "50%", width: 44, height: 44,
              transform: "translate(-50%,-50%)",
              background: "linear-gradient(135deg, #ddd6fe 0%, #a78bfa 100%)",
              boxShadow: "0 4px 18px rgba(124,58,237,.35), inset 0 0 12px rgba(255,255,255,.6)",
              animation: "ai-pulse 1.8s ease-in-out infinite",
            }}
          />
        </div>

        <h3 className="cs-heading text-base sm:text-lg font-semibold text-foreground tracking-tight">
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
          {subtitle}
        </p>
      </div>
    </div>
  );
}
